/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Tab, Tabs } from "@nordcloud/gnui";
import { EnvironmentType } from "~/generated/graphql";
import { SpendsChart } from "~/components";
import { useLexicalTabsWithUrl } from "~/hooks";
import { ACTIONS, useAccess } from "~/services/auth";
import { Maybe } from "~/tools";
import { RouteParams } from "~/views/environments/types";
import { CostDetailsTab } from "./CostDetailsTab";
import { EnvironmentBudgetTab } from "./EnvironmentBudgetTab";
import { EnvironmentCostAnalysisSection } from "./EnvironmentCostAnalysisSection";
import { KpiDetailsTab } from "./KpiDetailsTab";

type Props = {
  currentMonthCosts: string[];
  environmentType: Maybe<EnvironmentType>;
  environmentName: Maybe<string>;
  events: React.ComponentProps<typeof SpendsChart>["events"];
  forecast: number;
};

export function EnvironmentCostTabs({
  currentMonthCosts,
  environmentType,
  environmentName,
  events,
  forecast,
}: Props) {
  const { checkAccess } = useAccess();
  const { activeTab, setActiveTab, registerTabs } =
    useLexicalTabsWithUrl("costTab");
  const { environment } = useParams<RouteParams>();

  // Environments with name "default" are not real environments, so we don't collect KPIs for them.
  const showKpiForEnvironment =
    checkAccess(ACTIONS.viewEnvironmentKpi) && environmentName !== "default";

  useEffect(() => {
    registerTabs([
      "analysis",
      ...(showKpiForEnvironment ? ["kpi"] : []),
      "budget",
      "cost",
    ]);
    // we don't want registerTabs to avoid excessive recursion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKpiForEnvironment]);

  return (
    <Tabs step={activeTab} handleTab={setActiveTab}>
      <Tab heading="Cost analysis">
        <EnvironmentCostAnalysisSection
          envId={environment}
          forecast={forecast}
        />
      </Tab>
      {showKpiForEnvironment && (
        <Tab heading="KPI Details">
          <KpiDetailsTab environmentType={environmentType} />
        </Tab>
      )}
      <Tab heading="Budget">
        <EnvironmentBudgetTab envId={environment} />
      </Tab>
      <Tab heading="Cost details">
        <CostDetailsTab currentMonthCosts={currentMonthCosts} events={events} />
      </Tab>
    </Tabs>
  );
}
